import React, {
  createContext,
  ReactNode,
  useContext,
  useCallback,
} from 'react';
import { useTenant } from './TenantContext';
import { toUrlFromQuery } from '../utils';
import {
  OtpSendRequest,
  OtpSendResponse,
  OtpVerifyRequest,
  OtpVerifyResponse,
  ListingPreviewResponse,
  SetViewResponse,
} from '../system/preview';

interface PreviewServiceContextType {
  sendOtp: (request: OtpSendRequest) => Promise<OtpSendResponse>;
  verifyOtp: (request: OtpVerifyRequest) => Promise<OtpVerifyResponse>;
  getListingPreview: (
    listingKey: string,
    token?: string,
  ) => Promise<ListingPreviewResponse>;
  setView: (listingKey: string, token: string) => Promise<SetViewResponse>;
}

const PreviewServiceContext = createContext<
  PreviewServiceContextType | undefined
>(undefined);

async function handleHTTPError(res: Response) {
  if (!res.ok) {
    let body: any;
    try {
      body = await res.json();
    } catch {
      body = { error: `HTTP ${res.status}` };
    }
    throw body;
  }
}

export const PreviewServiceProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { api, tenant } = useTenant();

  const publicGet = useCallback(
    async <T,>(endpoint: string, query: Record<string, any>): Promise<T> => {
      const res = await fetch(
        toUrlFromQuery(`${api}/${endpoint}`, { ...query, system: tenant }),
        {
          credentials: 'include',
        },
      );
      await handleHTTPError(res);
      return (await res.json()) as T;
    },
    [api, tenant],
  );

  const publicPost = useCallback(
    async <T,>(endpoint: string, body: Record<string, any>): Promise<T> => {
      const res = await fetch(
        toUrlFromQuery(`${api}/${endpoint}`, { system: tenant }),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          credentials: 'include',
        },
      );
      await handleHTTPError(res);
      return (await res.json()) as T;
    },
    [api, tenant],
  );

  const sendOtp = useCallback(
    (request: OtpSendRequest) =>
      publicPost<OtpSendResponse>('share-draft-otp-send', request),
    [publicPost],
  );

  const verifyOtp = useCallback(
    (request: OtpVerifyRequest) =>
      publicPost<OtpVerifyResponse>('share-draft-otp-verify', request),
    [publicPost],
  );

  const getListingPreview = useCallback(
    (listingKey: string, token?: string) =>
      publicGet<ListingPreviewResponse>('get-listing-preview', {
        listingKey,
        ...(token ? { token } : {}),
      }),
    [publicGet],
  );

  const setView = useCallback(
    (listingKey: string, token: string) =>
      publicPost<SetViewResponse>('share-draft-set-view', {
        listingKey,
        token,
      }),
    [publicPost],
  );

  return (
    <PreviewServiceContext.Provider
      value={{ sendOtp, verifyOtp, getListingPreview, setView }}
    >
      {children}
    </PreviewServiceContext.Provider>
  );
};

export const usePreviewService = (): PreviewServiceContextType => {
  const context = useContext(PreviewServiceContext);
  if (context === undefined) {
    throw new Error(
      'usePreviewService must be used within a PreviewServiceProvider',
    );
  }
  return context;
};
